import { setTheme } from "@/helpers/setTheme";
import { Size, Theme } from "@/types";
import { ComponentProps, FC } from "react";
import cn from "classnames";
import Link from "@/components/Link";
import {
  CaretDownLine,
  CaretLeftLine,
  CaretRightLine,
  CaretUpLine,
  Filter,
  SpinnerDots,
  CaretDownSolid,
  ArrowLeftCircleLine,
  NewTab,
} from "@/icons";

type BaseButtonProps = {
  size?: Size;
  /**
   * Explicitly set the theme only if it needs to be overridden.
   */
  theme?: Theme;
  loading?: boolean;
  disabled?: boolean;
};

type LinkElementProps = Parameters<typeof Link>[number];

type ButtonElementProps = ComponentProps<"button">;

type ElementProps = LinkElementProps | ButtonElementProps;
type VariantProps = { component: FC<ElementProps> };

// paddings are the same for primary and secondary buttons
const PRIMARY_AND_SECONDARY_STYLES = {
  xs: "py-2 px-4",
  s: "py-3.5 px-6.5",
  m: "py-3.5 px-7.5 sm:py-4",
  l: "py-4.5 px-8 sm:py-5 sm:px-9",
  responsiveXsToS: "py-2 px-4 xl:py-3.5 xl:px-6.5",
};

const PRIMARY_AND_SECONDARY_STYLES_WITH_RIGHT_ARROW = {
  xs: "py-2 pl-4 pr-3",
  s: "py-3.5 pl-6.5 pr-5",
  m: "py-3.5 px-7.5 sm:py-4",
  l: "py-4.5 px-8 sm:py-5 sm:px-9",
  responsiveXsToS: "py-2 pl-4 pr-3 xl:py-3.5 xl:pl-6.5 xl:pr-5",
};

// font sizes are the same for all variants
const SHARED_STYLES = {
  xs: "text-link-xs",
  s: "text-link-s",
  m: "text-link-m-mobile sm:text-link-m",
  l: "text-link-l-mobile sm:text-link-l",
  responsiveXsToS: "text-link-xs xl:text-link-s",
};

const SPINNER_STYLES = {
  xs: "size-5",
  s: "size-5",
  m: "size-6",
  l: "size-8",
  responsiveXsToS: "size-5",
};

type InternalButtonProps<T> = ElementProps & T & VariantProps;

type PrimaryButtonProps = BaseButtonProps & {
  icon?: "arrowRight" | "none";
};

/**
 * DO NOT EXPORT!
 * Please use the `Button` component instead.
 */
const PrimaryButton: FC<InternalButtonProps<PrimaryButtonProps>> = ({
  size = "m",
  theme,
  icon = "none",
  component: Component,
  className,
  loading,
  disabled,
  ...props
}) => {
  return (
    <Component
      {...setTheme(theme)}
      {...props}
      disabled={disabled || loading}
      className={cn(
        "group text-button-primary-content bg-button-primary-fill hover:text-button-primary-content-hover hover:bg-button-primary-fill-hover active:text-button-primary-content-pressed active:bg-button-primary-fill-pressed disabled:text-button-primary-content-disabled disabled:bg-button-primary-fill-disabled font-geist rounded-[40px] font-semibold outline-none disabled:cursor-not-allowed",
        icon === "arrowRight" &&
          "disabled:bg-swdc-gray-100 black:disabled:bg-button-primary-content-disabled orange:disabled:bg-button-primary-fill-disabled dark:disabled:bg-button-primary-fill-disabled midtone:disabled:bg-button-primary-fill-disabled",
        "focus-visible:shadow-[inset_0_0_0_2.5px_var(--color-button-primary-fill),inset_0_0_0_4.5px_white,inset_0_0_0_6.5px_var(--color-swdc-blue-325)]",
        "hover:focus-visible:shadow-[inset_0_0_0_2.5px_var(--color-button-primary-fill-hover),inset_0_0_0_4.5px_white,inset_0_0_0_6.5px_var(--color-swdc-blue-325)]",
        "active:focus-visible:shadow-[inset_0_0_0_2.5px_var(--color-button-primary-fill-pressed),inset_0_0_0_4.5px_white,inset_0_0_0_6.5px_var(--color-swdc-blue-325)]",
        icon === "arrowRight"
          ? PRIMARY_AND_SECONDARY_STYLES_WITH_RIGHT_ARROW[size]
          : PRIMARY_AND_SECONDARY_STYLES[size],
        SHARED_STYLES[size],
        loading &&
          "text-button-primary-content-pressed! bg-button-primary-fill-pressed! pointer-events-none",
        className,
      )}
      aria-busy={loading}
    >
      <span className="relative flex items-center justify-center">
        {loading && (
          <SpinnerDots
            className={cn("absolute", SPINNER_STYLES[size], "animate-spin")}
          />
        )}
        <span
          className={cn(
            "flex items-center justify-center",
            loading && "invisible",
          )}
        >
          {props.children}
          {/* 3.4 design has only "right" icon position for Primary CTA */}
          {icon === "arrowRight" ? (
            <span
              aria-hidden
              className={cn(
                "text-swdc-orange-400 group-hover:text-swdc-black-600 group-active:text-swdc-black-600 orange:group-hover:text-swdc-orange-400 orange:group-active:text-swdc-orange-400 group-disabled:text-swdc-white! orange:group-disabled:text-swdc-orange-400! dark:group-disabled:text-button-primary-content-disabled! midtone:group-disabled:text-button-primary-content-disabled! relative ml-1.25 inline-flex h-5 shrink-0 items-center overflow-hidden after:absolute after:top-1/2 after:left-0 after:h-0.5 after:origin-left after:-translate-y-1/2 after:scale-x-0 after:bg-current after:transition-transform after:duration-260 after:ease-linear group-hover:after:scale-x-100 group-disabled:after:scale-x-0!",
                PRIMARY_AND_SECONDARY_ARROW_LINE_WIDTH[size],
              )}
            >
              <CaretRightLine
                fill="currentColor"
                className={cn(
                  ARROW_ICON_SIZE[size],
                  "absolute top-1/2 left-0 z-10 h-auto -translate-y-1/2 transition-transform duration-260 ease-linear group-hover:translate-x-1 group-disabled:translate-x-0!",
                )}
              />
            </span>
          ) : null}
        </span>
      </span>
    </Component>
  );
};

const SECONDARY_STYLES = {
  xs: "outline-2 -outline-offset-2",
  s: "outline-2 -outline-offset-2",
  m: "outline-3 -outline-offset-3",
  l: "outline-4 -outline-offset-4",
  responsiveXsToS:
    "outline-2 -outline-offset-2 xl:outline-3 xl:-outline-offset-3",
};

type SecondaryButtonProps = BaseButtonProps & {
  icon?: "arrowRight" | "none";
};

/**
 * DO NOT EXPORT!
 * Please use the `Button` component instead.
 */
const SecondaryButton: FC<InternalButtonProps<SecondaryButtonProps>> = ({
  size = "m",
  theme,
  icon = "none",
  component: Component,
  className,
  loading,
  disabled,
  ...props
}) => {
  return (
    <Component
      {...setTheme(theme)}
      {...props}
      disabled={disabled || loading}
      className={cn(
        "group font-geist text-button-secondary-content outline-button-secondary-outline hover:text-button-secondary-content-hover hover:outline-button-secondary-outline-hover active:text-button-secondary-content-pressed active:outline-button-secondary-outline-pressed disabled:text-button-secondary-content-disabled disabled:outline-button-secondary-outline-disabled rounded-[40px] font-semibold outline-solid disabled:cursor-not-allowed",
        "focus-visible:shadow-[inset_0_0_0_2.5px_var(--color-button-secondary-outline),inset_0_0_0_4.5px_white,inset_0_0_0_6.5px_var(--color-swdc-blue-325)]",
        "hover:focus-visible:shadow-[inset_0_0_0_2.5px_var(--color-button-secondary-outline-hover),inset_0_0_0_4.5px_white,inset_0_0_0_6.5px_var(--color-swdc-blue-325)]",
        "active:focus-visible:shadow-[inset_0_0_0_2.5px_var(--color-button-secondary-outline-pressed),inset_0_0_0_4.5px_white,inset_0_0_0_6.5px_var(--color-swdc-blue-325)]",
        icon === "arrowRight"
          ? PRIMARY_AND_SECONDARY_STYLES_WITH_RIGHT_ARROW[size]
          : PRIMARY_AND_SECONDARY_STYLES[size],
        SHARED_STYLES[size],
        SECONDARY_STYLES[size],
        loading &&
          "text-button-secondary-content-pressed! outline-button-secondary-outline-pressed! pointer-events-none",
        className,
      )}
      aria-busy={loading}
    >
      <span className="relative flex items-center justify-center">
        {loading && (
          <SpinnerDots
            className={cn("absolute", SPINNER_STYLES[size], "animate-spin")}
          />
        )}
        <span
          className={cn(
            "flex items-center justify-center",
            loading && "invisible",
          )}
        >
          {props.children}
          {/* 3.4 design has only "right" icon position for Secondary CTA */}
          {icon === "arrowRight" ? (
            <span
              aria-hidden
              className={cn(
                "text-button-secondary-outline-hover group-hover:text-button-secondary-outline-hover group-active:text-button-secondary-outline-pressed orange:group-hover:text-swdc-black-600 orange:group-active:text-swdc-black-600 group-disabled:text-swdc-gray-200! orange:group-disabled:text-swdc-orange-200! dark:group-disabled:text-button-secondary-content-disabled! midtone:group-disabled:text-button-secondary-content-disabled! relative ml-1.25 inline-flex h-5 shrink-0 items-center overflow-hidden after:absolute after:top-1/2 after:left-0 after:h-0.5 after:origin-left after:-translate-y-1/2 after:scale-x-0 after:bg-current after:transition-transform after:duration-260 after:ease-linear group-hover:after:scale-x-100 group-disabled:after:scale-x-0!",
                PRIMARY_AND_SECONDARY_ARROW_LINE_WIDTH[size],
              )}
            >
              <CaretRightLine
                fill="currentColor"
                className={cn(
                  ARROW_ICON_SIZE[size],
                  "absolute top-1/2 left-0 z-10 h-auto -translate-y-1/2 transition-transform duration-260 ease-linear group-hover:translate-x-1 group-disabled:translate-x-0!",
                )}
              />
            </span>
          ) : null}
        </span>
      </span>
    </Component>
  );
};

const ICON_PADDING = {
  left: "mr-1.25",
  right: "ml-1.25",
};

const TERTIARY_CONTENT_SPACING = {
  xs: "gap-0.5",
  s: "gap-0.5",
  m: "gap-1",
  l: "gap-1.5",
  responsiveXsToS: "gap-0.5",
};

const ARROW_ICON_SIZE = {
  xs: "w-[15px]",
  s: "w-[15px]",
  m: "w-[21px]",
  l: "w-[21px]",
  responsiveXsToS: "w-[15px]",
};

const PRIMARY_AND_SECONDARY_ARROW_LINE_WIDTH = {
  xs: "w-4 after:w-3",
  s: "w-4 after:w-3",
  m: "w-5 after:w-4",
  l: "w-5 after:w-4",
  responsiveXsToS: "w-4 after:w-3",
};

const TRIANGLE_ARROW_ICON_SIZE = {
  xs: "w-[14px]",
  s: "w-[14px]",
  m: "w-[18px]",
  l: "w-[20px]",
  responsiveXsToS: "w-[14px]",
};

const CIRCLE_ARROW_ICON_SIZE = {
  xs: 17,
  s: 17,
  m: 18,
  l: 23,
  responsiveXsToS: 17,
};
type TertiaryButtonProps = BaseButtonProps & {
  iconPosition?: "left" | "right";
  icon?:
    | "arrowRight"
    | "arrowDown"
    | "arrowLeft"
    | "arrowUp"
    | "circleArrowRight"
    | "circleArrowLeft"
    | "circleArrowUp"
    | "circleArrowDown"
    | "newTab"
    | "solidArrowLeft"
    | "none";
  renderLeftBorder?: boolean;
};

/**
 * DO NOT EXPORT!
 * Please use the `Button` component instead.
 */
const TertiaryButton: FC<InternalButtonProps<TertiaryButtonProps>> = ({
  size = "m",
  theme,
  icon = "arrowRight",
  iconPosition = "right",
  component: Component,
  className,
  renderLeftBorder,
  ...props
}) => {
  let leftContent = null,
    rightContent = null;
  let iconComponent = null;

  switch (icon) {
    case "arrowDown":
      iconComponent = (
        <CaretDownSolid
          className={cn("inline shrink-0", TRIANGLE_ARROW_ICON_SIZE[size])}
          fill="currentColor"
        />
      );
      break;
    case "arrowLeft":
      iconComponent = (
        <CaretLeftLine
          className={cn("inline shrink-0", ARROW_ICON_SIZE[size])}
          fill="currentColor"
        />
      );
      break;
    case "arrowRight":
      iconComponent = (
        <CaretRightLine
          className={cn("inline shrink-0", ARROW_ICON_SIZE[size])}
          fill="currentColor"
        />
      );
      break;
    case "arrowUp":
      iconComponent = (
        <CaretDownSolid
          className={cn(
            "inline shrink-0 rotate-180",
            TRIANGLE_ARROW_ICON_SIZE[size],
          )}
          fill="currentColor"
        />
      );
      break;
    case "solidArrowLeft":
      iconComponent = (
        <CaretDownSolid
          className={cn(
            "inline shrink-0 rotate-90",
            TRIANGLE_ARROW_ICON_SIZE[size],
          )}
          fill="currentColor"
        />
      );
      break;
    case "circleArrowLeft":
      iconComponent = (
        <ArrowLeftCircleLine
          className={cn("inline shrink-0 rounded-full")}
          height={CIRCLE_ARROW_ICON_SIZE[size]}
          width={CIRCLE_ARROW_ICON_SIZE[size]}
          fill="currentColor"
        />
      );
      break;
    case "circleArrowRight":
      iconComponent = (
        <ArrowLeftCircleLine
          className={cn("inline shrink-0 rotate-180 rounded-full")}
          height={CIRCLE_ARROW_ICON_SIZE[size]}
          width={CIRCLE_ARROW_ICON_SIZE[size]}
          fill="currentColor"
        />
      );
      break;
    case "circleArrowUp":
      iconComponent = (
        <ArrowLeftCircleLine
          className={cn("inline shrink-0 rotate-90 rounded-full")}
          height={CIRCLE_ARROW_ICON_SIZE[size]}
          width={CIRCLE_ARROW_ICON_SIZE[size]}
          fill="currentColor"
        />
      );
      break;
    case "circleArrowDown":
      iconComponent = (
        <ArrowLeftCircleLine
          className={cn("inline shrink-0 -rotate-90 rounded-full")}
          height={CIRCLE_ARROW_ICON_SIZE[size]}
          width={CIRCLE_ARROW_ICON_SIZE[size]}
          fill="currentColor"
        />
      );
      break;
    case "newTab":
      iconComponent = (
        <NewTab width={18} className="ml-0.5 shrink-0 pb-0.5" aria-hidden />
      );
      break;
  }

  if (iconPosition === "left") {
    leftContent = iconComponent;
  } else {
    rightContent = iconComponent;
  }

  return (
    <Component
      {...setTheme(theme)}
      {...props}
      className={cn(
        renderLeftBorder &&
          "border-l-swdc-gray-300 hover:before:bg-new-tertiary-content active:before:bg-new-tertiary-content-pressed relative border-l pl-5 before:absolute before:inset-y-0 before:left-0 before:w-0 before:rounded-3xl before:content-[''] hover:border-l-transparent hover:no-underline hover:before:w-0.75 hover:before:shadow-[0_0_16px_4px_rgba(255,255,255,0.75)] active:border-l-transparent active:no-underline active:before:w-0.75 active:before:shadow-[0_0_16px_4px_rgba(255,255,255,0.75)]",
        !renderLeftBorder && "hover:underline",
        "text-button-tertiary-content hover:text-button-tertiary-content-hover active:text-button-tertiary-content-pressed font-geist px-0.5 outline-none focus-visible:rounded-sm focus-visible:shadow-[inset_0_0_0_2px_var(--color-swdc-blue-325),0_0_0_2px_white] disabled:no-underline",
        "disabled:text-button-tertiary-content-disabled disabled:cursor-not-allowed",
        SHARED_STYLES[size],
        size !== "s" && size !== "xs" && "font-semibold",
        icon.includes("circle") &&
          "[&_svg]:bg-button-tertiary-icon-bg active:[&_svg]:bg-button-tertiary-icon-bg-pressed disabled:[&_svg]:bg-button-tertiary-icon-bg-disabled black:[&_svg]:bg-button-tertiary-arrow black:active:[&_svg]:bg-transparent black:disabled:[&_svg]:bg-transparent",
        className,
      )}
    >
      <span
        className={cn(
          "flex flex-row items-center",
          TERTIARY_CONTENT_SPACING[size],
        )}
      >
        {leftContent}
        <span className={cn(leftContent && "text-left")}>{props.children}</span>
        {rightContent}
      </span>
    </Component>
  );
};

type IndexButtonProps = BaseButtonProps & {
  iconPosition?: "left" | "right";
  icon?:
    "none" | "arrowRight" | "arrowDown" | "arrowLeft" | "arrowUp" | "filter";
};
/**
 * DO NOT EXPORT!
 * Please use the `Button` component instead.
 */
const IndexButton: FC<InternalButtonProps<IndexButtonProps>> = ({
  size = "m",
  theme,
  icon = "arrowRight",
  iconPosition = "right",
  component: Component,
  className,
  ...props
}) => {
  let leftContent = null,
    rightContent = null;
  let iconComponent = null;

  switch (icon) {
    case "arrowDown":
      iconComponent = (
        <CaretDownLine
          className={cn(
            "inline",
            ICON_PADDING[iconPosition],
            ARROW_ICON_SIZE[size],
          )}
          fill="currentColor"
        />
      );
      break;
    case "arrowLeft":
      iconComponent = (
        <CaretLeftLine
          className={cn(
            "inline",
            ICON_PADDING[iconPosition],
            ARROW_ICON_SIZE[size],
          )}
          fill="currentColor"
        />
      );
      break;
    case "arrowRight":
      iconComponent = (
        <CaretRightLine
          className={cn(
            "inline",
            ICON_PADDING[iconPosition],
            ARROW_ICON_SIZE[size],
          )}
          fill="currentColor"
        />
      );
      break;
    case "arrowUp":
      iconComponent = (
        <CaretUpLine
          className={cn(
            "inline",
            ICON_PADDING[iconPosition],
            ARROW_ICON_SIZE[size],
          )}
          fill="currentColor"
        />
      );
      break;
    case "filter":
      iconComponent = <Filter className="w-4" color="currentColor" />;
      break;
  }

  if (iconPosition === "left") {
    leftContent = iconComponent;
  } else {
    rightContent = iconComponent;
  }

  return (
    <Component
      {...setTheme(theme)}
      {...props}
      className={cn(
        "bg-background-highlight rounded-corner-subtle border-swdc-gray-200 text-tab-link hover:text-tab-link-hover active:text-tab-link-pressed border-1 border-solid px-4 py-2 hover:underline disabled:cursor-not-allowed",
        SHARED_STYLES[size],
        icon.includes("circle") &&
          "[&_svg]:bg-button-tertiary-arrow active:[&_svg]:bg-transparent disabled:[&_svg]:bg-transparent",
        className,
      )}
    >
      <span className="flex flex-row items-center justify-between gap-x-2">
        {leftContent}
        {props.children}
        {rightContent}
      </span>
    </Component>
  );
};

export type ButtonProps = ElementProps &
  (
    | ({ variant: "primary" } & PrimaryButtonProps)
    | ({ variant: "secondary" } & SecondaryButtonProps)
    | ({ variant: "tertiary" } & TertiaryButtonProps)
    | ({ variant: "index" } & IndexButtonProps)
  );

/**
 * General `Button` element (aka the CTA).
 * @param {("primary"|"secondary"|"tertiary"|"index")=} [variant="primary"] the options are `primary`, `secondary`, `tertiary` for common usages, with `primary` being the default one. `index` is inteded for index pages usages, such as filtering or sorting.
 * @param {("white"|"gray"|"black"|"orange")=} theme dependent on the container's theme. DO NOT PROVIDE THIS ARGUMENT UNLESS NECESSARY. The options are `white`, `gray`, `black` and `orange`.
 * @returns an `a` element or `button` element based on the chosen type with appropriate styles applied based on the provided `variant` and `theme`.
 */
export const Button: FC<ButtonProps> = (props) => {
  const Element: FC<ElementProps> = (innerProps) => {
    if ("href" in innerProps && Boolean(innerProps.href)) {
      return <Link {...innerProps} />;
    } else {
      const { href: _href, ...buttonProps } =
        innerProps as ButtonElementProps & {
          href?: string;
        };

      return <button {...buttonProps} />;
    }
  };

  switch (props.variant) {
    case "primary": {
      return <PrimaryButton component={Element} {...props} />;
    }
    case "secondary": {
      return <SecondaryButton component={Element} {...props} />;
    }
    case "tertiary":
      return <TertiaryButton component={Element} {...props} />;
    case "index":
      return <IndexButton component={Element} {...props} />;
  }
};
